/** Admin: free demo classes (requests from students, arranging a class on any topic) and paid resume-building requests. */
import { api } from '../../core/api.js';
import { register } from '../../core/events.js';
import { html } from '../../core/html.js';
import { fmtDate, fmtDateTime } from '../../core/format.js';
import { currentView, reload } from '../../app/shell.js';
import { banner, empty, ic, pageHead, statusTag, table, tag } from '../../ui/components.js';
import { field, selectField } from '../../ui/forms.js';
import { closeModal, openModal } from '../../ui/modal.js';
import { submitForm } from '../../ui/submit.js';
import { toast } from '../../ui/toast.js';

const trainerOptions = (list) => list.map((t) => [t.id, `${t.name} (${t.email})`]);

/* ---------- demo classes ---------- */

const demos = {
  path: '/admin/demos',
  roles: ['admin'],
  title: 'Free demos',
  async load(ctx) {
    const [r, c, t] = await Promise.all([
      api.get('/admin/demo-requests', { signal: ctx.signal, query: { limit: 100 } }),
      api.get('/admin/demo-classes', { signal: ctx.signal }),
      api.get('/admin/users', { signal: ctx.signal, query: { role: 'professional', verification: 'verified', status: 'active', limit: 100 } }),
    ]);
    return { requests: r.requests, classes: c.classes, trainers: t.users };
  },
  view(d) {
    const waiting = d.requests.filter((r) => r.status === 'requested');
    const approved = d.requests.filter((r) => r.status === 'approved' && !r.batch_id);
    return html`${pageHead('Free demo classes', 'Students ask for a demo. You approve, then arrange a free class on any topic.',
      html`<button type="button" class="btn" data-act="demo.arrange">${ic('plus')} Arrange a demo class</button>`)}
      ${waiting.length ? banner(`${waiting.length} request${waiting.length === 1 ? '' : 's'} waiting for you.`, 'wn', 'info') : ''}
      <h3>Requests</h3>
      ${d.requests.length ? table(['Student', 'Topic', 'Asked', 'Status', ''], d.requests.map((r) => [
        html`<b>${r.student}</b><br><span class="xs mu">${r.email}</span>`,
        html`${r.topic}${r.note ? html`<br><span class="xs mu">${r.note}</span>` : ''}`, fmtDate(r.created_at), statusTag(r.status),
        r.status === 'requested'
          ? html`<div class="row nw"><button type="button" class="btn sm" data-act="demo.decide" data-id="${r.id}" data-name="${r.student}" data-action="approve">Approve</button>
            <button type="button" class="btn ghost sm" data-act="demo.decide" data-id="${r.id}" data-name="${r.student}" data-action="decline">Decline</button></div>`
          : r.status === 'approved' && !r.batch_id ? tag('Needs a class', 'wn') : ''])) : empty('cal', 'No requests yet', 'When a student asks for a demo it appears here.')}
      ${approved.length ? banner(`${approved.length} approved student${approved.length === 1 ? ' is' : 's are'} not in a class yet. Arrange a class and pick them.`, '', 'info') : ''}
      <h3 class="mt">Demo classes</h3>
      ${d.classes.length ? table(['Topic', 'Starts', 'Seats left', 'Trainer', 'Status'], d.classes.map((c) => [c.topic, fmtDateTime(c.starts_at), `${c.seats_left} of ${c.capacity}`,
        c.trainer_id ? statusTag(c.trainer_status) : tag('No trainer', 'wn'), statusTag(c.status)])) : empty('play', 'No demo classes yet', 'Arrange one to get started.')}`;
  },
};

register({
  'demo.decide': (el) => {
    const approve = el.dataset.action === 'approve';
    const d = currentView().data;
    openModal({
      title: `${approve ? 'Approve' : 'Decline'} ${el.dataset.name}`,
      body: html`<form id="demo-decide" data-submit="demo.decide.send" data-id="${el.dataset.id}" data-action="${el.dataset.action}" novalidate>
        ${approve ? selectField('dd-batch', 'Place in a demo class now', d.classes.filter((c) => c.status === 'scheduled' && c.seats_left > 0).map((c) => [c.batch_id, `${c.topic}, ${fmtDateTime(c.starts_at)}`]), { name: 'batch_id', req: false, placeholder: 'Decide later', help: 'Leave empty to approve now and arrange the class afterwards.' }) : ''}
        ${field('dd-note', approve ? 'Note (optional)' : 'Reason (the student is told)', { name: 'note', req: !approve, rows: 2, max: 300 })}</form>`,
      foot: html`<button type="button" class="btn ghost" data-act="modal.close">Cancel</button><button type="submit" form="demo-decide" class="btn ${approve ? '' : 'danger'}">${approve ? 'Approve' : 'Decline'}</button>`,
    });
  },
  'demo.decide.send': (form) => submitForm(form, async ({ batch_id: batch, note }) => {
    const body = { action: form.dataset.action, note: note || '' };
    if (batch) body.batch_id = Number(batch);
    await api.post(`/admin/demo-requests/${form.dataset.id}/decide`, { body });
    closeModal(false);
    toast(form.dataset.action === 'approve' ? 'Approved. The student is told.' : 'Declined. The student is told.', 'check');
    await reload();
  }, { onError: (error) => toast(error.message, 'alert') }),
  'demo.arrange': () => {
    const d = currentView().data;
    const waiting = d.requests.filter((r) => r.status === 'requested' || (r.status === 'approved' && !r.batch_id));
    openModal({
      title: 'Arrange a free demo class',
      wide: true,
      body: html`<form id="demo-arrange" data-submit="demo.arrange.send" novalidate>
        ${field('da-topic', 'Topic', { name: 'topic', minlen: 3, maxlength: 140, ph: 'Introduction to Selenium' })}
        ${field('da-desc', 'What students will learn', { name: 'description', rows: 3, req: false, max: 2000 })}
        <div class="fgrid">${field('da-day', 'Date', { name: 'day', type: 'date' })}${field('da-time', 'Start time (India time)', { name: 'start_time', type: 'time', val: '18:00' })}</div>
        <div class="fgrid">${field('da-dur', 'Length (minutes)', { name: 'duration_min', type: 'number', min: 15, max: 240, val: 60 })}${field('da-cap', 'Seats', { name: 'capacity', type: 'number', min: 1, max: 500, val: 100 })}</div>
        ${selectField('da-trainer', 'Trainer (optional)', trainerOptions(d.trainers), { name: 'trainer_id', req: false, placeholder: 'Decide later', help: 'The trainer gets an offer and must accept it.' })}
        ${waiting.length ? html`<fieldset class="field"><legend class="sm"><b>Place these students in the class</b> <span class="mu">(approves any still waiting)</span></legend>
          ${waiting.map((r) => html`<label class="row sm" style="gap:8px"><input type="checkbox" name="req_${r.id}" value="${r.id}"> ${r.student}: ${r.topic}</label>`)}</fieldset>` : ''}</form>`,
      foot: html`<button type="button" class="btn ghost" data-act="modal.close">Cancel</button><button type="submit" form="demo-arrange" class="btn">Arrange class</button>`,
    });
  },
  'demo.arrange.send': (form) => submitForm(form, async (v) => {
    const ids = [...form.querySelectorAll('input[type=checkbox]:checked')].map((x) => Number(x.value));
    const body = { topic: v.topic, description: v.description || '', day: v.day, start_time: v.start_time, duration_min: Number(v.duration_min) || 60, capacity: Number(v.capacity) || 30, request_ids: ids };
    if (v.trainer_id) body.trainer_id = Number(v.trainer_id);
    const out = await api.post('/admin/demo-classes', { body });
    closeModal(false);
    toast(out.placed.length ? `Demo class arranged. ${out.placed.length} student${out.placed.length === 1 ? '' : 's'} added and told.` : 'Demo class arranged.', 'check');
    await reload();
  }, { onError: (error) => toast(error.message, 'alert') }),
});

/* ---------- resume-building requests ---------- */

const resumeRequests = {
  path: '/admin/resume-requests',
  roles: ['admin'],
  title: 'Resume requests',
  load: (ctx) => api.get('/admin/resume-requests', { signal: ctx.signal, query: { limit: 100 } }),
  view(d) {
    const todo = d.requests.filter((r) => r.status === 'paid').length;
    return html`${pageHead('Resume building requests', 'Students and freelancers pay first. Build the resume, then upload the finished PDF.')}
      ${todo ? banner(`${todo} paid request${todo === 1 ? '' : 's'} to build.`, 'wn', 'info') : ''}
      ${d.requests.length ? table(['Person', 'What they asked for', 'Asked', 'Status', ''], d.requests.map((r) => [
        html`<b>${r.student}</b><br><span class="xs mu">${r.email}</span>`,
        html`<span style="white-space:pre-line">${r.brief}</span>`, fmtDate(r.created_at),
        r.status === 'paid' ? tag('Paid, to build', 'wn') : r.status === 'delivered' ? tag('Delivered', 'ok') : tag('Waiting for payment', 'n'),
        html`<div class="row nw">${r.current_resume_file_id ? html`<button type="button" class="btn ghost sm" data-act="file.open" data-id="${r.current_resume_file_id}">${ic('file')} Their resume</button>` : ''}
          ${r.status !== 'awaiting_payment' ? html`<button type="button" class="btn sm" data-act="resumereq.deliver" data-id="${r.id}" data-name="${r.student}">${r.status === 'delivered' ? 'Upload a new version' : 'Upload finished PDF'}</button>` : ''}</div>`]))
        : empty('file', 'No requests yet', 'Set the price in Settings to switch resume building help on.')}`;
  },
};

register({
  'resumereq.deliver': (el) => openModal({
    title: `Finished resume for ${el.dataset.name}`,
    body: html`<form id="resume-deliver" data-submit="resumereq.send" data-id="${el.dataset.id}" novalidate>
      <div class="field" data-req="true" data-type="file"><label for="rd-file">Finished resume (PDF)</label><input id="rd-file" name="file" type="file" accept=".pdf,application/pdf">
        <span class="help">It becomes the resume on their profile and they are told.</span><span class="err" role="alert">Choose a PDF file.</span></div></form>`,
    foot: html`<button type="button" class="btn ghost" data-act="modal.close">Cancel</button><button type="submit" form="resume-deliver" class="btn">Deliver</button>`,
  }),
  'resumereq.send': (form) => submitForm(form, async () => {
    const file = form.querySelector('input[type=file]').files[0];
    if (!file || !/\.pdf$/i.test(file.name)) throw new Error('Choose the finished resume as a PDF.');
    const body = new FormData();
    body.append('file', file);
    await api.upload(`/admin/resume-requests/${form.dataset.id}/deliver`, body);
    closeModal(false);
    toast('Delivered. The person is told.', 'check');
    await reload();
  }, { onError: (error) => toast(error.message, 'alert') }),
});

export const routes = [demos, resumeRequests];
